import { useMemo, useState } from 'react';
import { api, ProjectExtraction } from '../../api';
import { ExtractionRunsView, ExtractionSchedule, RUN_STATUS_LABEL, runStatusColor, summarizeRunCounts, type ExtractionRunsSource } from '../../../../shared/src';

interface ExtractionRunsPanelProps {
  projectId: string;
  extractions: ProjectExtraction[];
  ledgerConfigured: boolean;
}

/**
 * "Automated imports" view of the Uploads page: the extractions a superadmin
 * mapped to this project, each with its latest run, and the read-only run
 * history behind each one. Files imported this way live in the organization's
 * bucket under extractions/<key>/ and do not appear in the manual file list.
 */
export function ExtractionRunsPanel({ projectId, extractions, ledgerConfigured }: ExtractionRunsPanelProps) {
  const [selected, setSelected] = useState<ProjectExtraction | null>(extractions.length === 1 ? extractions[0] : null);

  const source = useMemo<ExtractionRunsSource | null>(() => {
    if (!selected) return null;
    const key = selected.key;
    return {
      listRuns: () => api.listExtractionRuns(key, projectId),
      getRun: (runId: string, cursor?: string) => api.getExtractionRun(key, runId, projectId, cursor),
    };
  }, [selected, projectId]);

  if (extractions.length === 0) {
    return <div className="extraction-runs-notice">No automated import is configured for this project.</div>;
  }

  if (selected && source) {
    return (
      <ExtractionRunsView
        extraction={selected}
        source={source}
        onBack={extractions.length > 1 ? () => setSelected(null) : undefined}
        backLabel="All imports"
      />
    );
  }

  return (
    <div className="extraction-runs">
      {!ledgerConfigured && <div className="extraction-runs-notice">Run history is not available in this environment yet.</div>}
      <table className="data-table extraction-runs-table">
        <thead>
          <tr>
            <th>Import</th>
            <th>Last run</th>
            <th>Status</th>
            <th>Files</th>
          </tr>
        </thead>
        <tbody>
          {extractions.map((e) => (
            <tr key={e.key} className="extraction-run-row" onClick={() => setSelected(e)}>
              <td>
                <button type="button" className="btn-link extraction-run-link">
                  {e.name}
                </button>
                {e.description && <div className="extraction-runs-subtitle">{e.description}</div>}
                <ExtractionSchedule extraction={e} />
              </td>
              <td>{e.latest_run ? new Date(e.latest_run.started_at).toLocaleString() : 'Never'}</td>
              <td>
                {e.latest_run ? (
                  <span className={`status-badge ${runStatusColor(e.latest_run.status)}`}>{RUN_STATUS_LABEL[e.latest_run.status]}</span>
                ) : (
                  '—'
                )}
              </td>
              <td className="extraction-run-counts">{e.latest_run ? summarizeRunCounts(e.latest_run.counts) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
